
import { observer } from "mobx-react-lite";
import React from "react";
import { useStore } from "../../../helpers/helpers";
import { htmlValidation } from "../../../helpers/utils";
import {
  UsersIcon,
  PencilSquareIcon,
  TrashIcon,
  RectangleStackIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";

interface TargetItem {
  id: string;
  name: string;
  count: number;
}

interface VoterReachOutListTargetProps {
  deleteMessage: (id: string) => void;
  setPopOver: (value: boolean) => void;
  editMessage: (item: TargetItem) => void;
}

const VoterReachOutListTarget = observer(
  (props: VoterReachOutListTargetProps) => {
    const { voterReachOutStore } = useStore();

    const {
      filteredReachoutTargetList,
    } = voterReachOutStore;

    const {
      deleteMessage,
      setPopOver,
      editMessage,
    } = props;

    const deleteVoterReachOutTarget = (
      e: React.MouseEvent<HTMLButtonElement>,
      id: string
    ) => {
      htmlValidation(e);
      e.stopPropagation();
      deleteMessage(id);
    };

    const detailsReachOutTarget = async (
      id: string,
      mode: string
    ) => {
      const detailsTargetPayload = { id };

      const response =
        await voterReachOutStore.details_reachout_target(
          detailsTargetPayload
        );

      if (response.status === 200) {
        if (mode === "edit") {
          voterReachOutStore.set_isEditOverlay(true);
        }
      }
    };

    const handleViewTarget = (
      e: React.MouseEvent<HTMLDivElement>,
      item: TargetItem
    ) => {
      htmlValidation(e);
      setPopOver(false);

      void detailsReachOutTarget(item.id, "view");

      voterReachOutStore.set_IsMessageOverviewOpen(true);
      voterReachOutStore.set_selected_overview_item(item);
    };

    if (
      !filteredReachoutTargetList ||
      filteredReachoutTargetList.length === 0
    ) {
      return (
        <div className="rounded-xl border border-slate-200 bg-white px-5 py-12 text-center">
          <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-lg bg-slate-50 text-slate-400">
            <RectangleStackIcon className="h-5 w-5" />
          </div>

          <h3 className="mt-3 text-sm font-semibold text-slate-800">
            No target groups found
          </h3>

          <p className="mx-auto mt-1 max-w-sm text-sm text-slate-500">
            Try a different search or create a new target group.
          </p>
        </div>
      );
    }

    return (
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        {/* List header */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3.5 sm:px-5">
          <div>
            <h2 className="text-sm font-semibold text-slate-800">
              Target Groups
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Select a target to view its details
            </p>
          </div>

          <span className="rounded-md bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
            {filteredReachoutTargetList.length}{" "}
            {filteredReachoutTargetList.length === 1
              ? "group"
              : "groups"}
          </span>
        </div>

        {/* Column labels - desktop */}
        <div className="hidden grid-cols-[minmax(0,1fr)_120px_100px] items-center gap-4 border-b border-slate-100 bg-slate-50/70 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 sm:grid">
          <span>Target</span>
          <span>Audience</span>
          <span className="text-right">Actions</span>
        </div>

        {/* Target rows */}
        <div className="divide-y divide-slate-100">
          {filteredReachoutTargetList.map(
            (item: TargetItem) => (
              <div
                key={item.id}
                className="group grid grid-cols-1 items-center gap-3 px-4 py-3.5 transition-colors hover:bg-slate-50/80 sm:grid-cols-[minmax(0,1fr)_120px_100px] sm:gap-4 sm:px-5"
              >
                {/* Target name and icon */}
                <div
                  role="button"
                  tabIndex={0}
                  aria-label={`View target ${item.name}`}
                  onClick={(e) => handleViewTarget(e, item)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setPopOver(false);
                      void detailsReachOutTarget(item.id, "view");
                      voterReachOutStore.set_IsMessageOverviewOpen(true);
                      voterReachOutStore.set_selected_overview_item(item);
                    }
                  }}
                  className="flex min-w-0 cursor-pointer items-center gap-3 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-colors group-hover:border-indigo-100 group-hover:bg-indigo-50 group-hover:text-indigo-600">
                    <UsersIcon className="h-4 w-4" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <p
                      title={item.name}
                      className="truncate text-sm font-medium text-slate-800 transition-colors group-hover:text-indigo-700"
                    >
                      {item.name}
                    </p>

                    <p className="mt-0.5 text-xs text-slate-500 sm:hidden">
                      Target group
                    </p>
                  </div>

                  <ChevronRightIcon className="h-4 w-4 shrink-0 text-slate-300 transition-colors group-hover:text-indigo-500 sm:hidden" />
                </div>

                {/* Audience count */}
                <div className="flex items-center justify-between pl-12 sm:pl-0">
                  <span className="text-xs text-slate-500 sm:hidden">
                    Audience
                  </span>

                  <span className="inline-flex items-center gap-1.5 text-sm font-medium tabular-nums text-slate-700">
                    <UsersIcon className="hidden h-3.5 w-3.5 text-slate-400 sm:block" />
                    {item.count || 0}
                    <span className="text-xs font-normal text-slate-500">
                      {(item.count || 0) === 1
                        ? "voter"
                        : "voters"}
                    </span>
                  </span>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-1 border-t border-slate-100 pt-2 sm:border-0 sm:pt-0">
                  <button
                    type="button"
                    aria-label={`Edit ${item.name}`}
                    title="Edit target"
                    onClick={(e) => {
                      htmlValidation(e);
                      e.stopPropagation();
                      editMessage(item);
                    }}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-indigo-50 hover:text-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  >
                    <PencilSquareIcon className="h-4 w-4" />
                  </button>

                  <button
                    type="button"
                    aria-label={`Delete ${item.name}`}
                    title="Delete target"
                    onClick={(e) =>
                      deleteVoterReachOutTarget(e, item.id)
                    }
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-200"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )
          )}
        </div>
      </section>
    );
  }
);

export default VoterReachOutListTarget;
